import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const TIME_RANGES = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '6 Months', value: 180 },
  { label: '1 Year', value: 365 },
  { label: '3 Years', value: 1095 },
  { label: '5 Years', value: 1825 }
];

const TYPE_COLORS = {
  advertisement: '#3B82F6', // blue
  event: '#10B981', // green
  deal: '#F59E0B', // amber
  local: '#8B5CF6' // purple
};

const STATUS_COLORS = {
  active: '#10B981',
  draft: '#6B7280',
  scheduled: '#3B82F6',
  expired: '#EF4444',
  archived: '#9CA3AF'
};

// Sort icon component - moved outside to avoid re-renders
const SortIcon = ({ field, sortBy, sortOrder }) => (
  <span className="ml-1 inline-block">
    {sortBy === field ? (
      sortOrder === 'desc' ? '↓' : '↑'
    ) : (
      <span className="text-gray-400">↕</span>
    )}
  </span>
);

const ExploreAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trends');
  const [timeRange, setTimeRange] = useState(30);
  const [sortBy, setSortBy] = useState('views');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchAnalytics = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [analyticsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/explore/admin/analytics`, { headers }),
        axios.get(`${API}/explore/admin/analytics/trends?days=${timeRange}`, { headers })
      ]);
      
      setAnalytics(analyticsRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (timeRange <= 30) {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    } else if (timeRange <= 365) {
      return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    }
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getTypeLabel = (type) => {
    const labels = { 
      advertisement: 'Advertisements', 
      event: 'Events', 
      deal: 'Deals', 
      local: 'Local' 
    };
    return labels[type] || type;
  };

  const getSortedPerformers = () => {
    if (!analytics?.top_performers) return [];
    return [...analytics.top_performers].sort((a, b) => {
      const multiplier = sortOrder === 'desc' ? -1 : 1;
      return (a[sortBy] - b[sortBy]) * multiplier;
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading Explore Analytics...</span>
        </div>
      </div>
    );
  }

  const byTypeData = analytics?.by_type ? Object.entries(analytics.by_type).map(([type, data]) => ({
    name: getTypeLabel(type),
    type,
    views: data.views,
    clicks: data.clicks,
    ctr: data.ctr,
    total: data.total,
    active: data.active
  })) : [];

  const byStatusData = analytics?.by_status ? Object.entries(analytics.by_status).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count,
    color: STATUS_COLORS[status]
  })).filter(d => d.value > 0) : [];

  const targetingData = analytics?.targeting_breakdown ? [
    { name: 'Geo-Targeted', value: analytics.targeting_breakdown.geo_targeted, color: '#8B5CF6' },
    { name: 'Category-Targeted', value: analytics.targeting_breakdown.category_targeted, color: '#3B82F6' },
    { name: 'Region-Targeted', value: analytics.targeting_breakdown.region_targeted, color: '#10B981' },
    { name: 'Untargeted (All)', value: analytics.targeting_breakdown.untargeted, color: '#6B7280' }
  ].filter(d => d.value > 0) : [];

  return (
    <div className="w-full space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Explore Analytics</h1>
              <p className="text-purple-200 text-sm">Track engagement and performance of Explore Hub content</p>
            </div>
          </div>
          
          {/* Time Range Selector */}
          <div className="flex items-center gap-2">
            <span className="text-purple-200 text-sm">Time Range:</span>
            <select
              value={timeRange}
              onChange={(e) => {
                setTimeRange(Number(e.target.value));
                setLoading(true);
              }}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-400"
            >
              {TIME_RANGES.map(range => (
                <option key={range.value} value={range.value} className="bg-gray-800">
                  {range.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      {analytics?.overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-blue-300">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-blue-50 border border-gray-200">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-blue-800">
                  {analytics.overview.total_content}
                </div>
              </div>
              <h3 className="text-gray-700 font-semibold text-sm">Total Content</h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-green-300">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-green-50 border border-gray-200">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-green-800">
                  {analytics.overview.total_active}
                </div>
              </div>
              <h3 className="text-gray-700 font-semibold text-sm">Active Content</h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-purple-300">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-purple-50 border border-gray-200">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-purple-800">
                  {formatNumber(analytics.overview.total_views)}
                </div>
              </div>
              <h3 className="text-gray-700 font-semibold text-sm">Total Views</h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-amber-300">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-amber-50 border border-gray-200">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-amber-800">
                  {formatNumber(analytics.overview.total_clicks)}
                </div>
              </div>
              <h3 className="text-gray-700 font-semibold text-sm">Total Clicks</h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-indigo-300">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-indigo-50 border border-gray-200">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-indigo-800">
                  {analytics.overview.overall_ctr}%
                </div>
              </div>
              <h3 className="text-gray-700 font-semibold text-sm">Overall CTR</h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-orange-300">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-orange-50 border border-gray-200">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-orange-800">
                  {analytics.overview.featured_count}
                </div>
              </div>
              <h3 className="text-gray-700 font-semibold text-sm">Featured</h3>
            </div>
          </div>
        </div>
      )}

      {/* Content Tabs */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'trends', label: '📈 Engagement Trends', icon: null },
              { id: 'performance', label: '📊 Performance by Type', icon: null },
              { id: 'top', label: '🏆 Top Performers', icon: null },
              { id: 'groups', label: '📁 By Group', icon: null },
              { id: 'targeting', label: '🎯 Targeting Insights', icon: null }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Engagement Trends Tab */}
          {activeTab === 'trends' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  Engagement Trends ({TIME_RANGES.find(r => r.value === timeRange)?.label})
                </h3>
                {trends?.totals && (
                  <div className="flex gap-4 text-sm">
                    <span className="text-purple-600 font-medium">
                      Period Views: {formatNumber(trends.totals.views)}
                    </span>
                    <span className="text-amber-600 font-medium">
                      Period Clicks: {formatNumber(trends.totals.clicks)}
                    </span>
                    <span className="text-indigo-600 font-medium">
                      Period CTR: {trends.totals.ctr}%
                    </span>
                  </div>
                )}
              </div>
              
              {trends?.trends && trends.trends.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={trends.trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        stroke="#6b7280"
                        tick={{ fontSize: 12 }}
                        interval={timeRange <= 30 ? 'preserveStartEnd' : Math.floor(trends.trends.length / 10)}
                      />
                      <YAxis yAxisId="left" stroke="#8B5CF6" />
                      <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        labelFormatter={(label) => new Date(label).toLocaleDateString('en-GB', { 
                          day: '2-digit', month: 'short', year: 'numeric' 
                        })}
                      />
                      <Legend />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="views" 
                        stroke="#8B5CF6" 
                        strokeWidth={2}
                        name="Views"
                        dot={timeRange <= 30 ? { fill: '#8B5CF6', r: 3 } : false}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="clicks" 
                        stroke="#F59E0B" 
                        strokeWidth={2}
                        name="Clicks"
                        dot={timeRange <= 30 ? { fill: '#F59E0B', r: 3 } : false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {/* Explanatory note */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-700">
                      <span className="font-semibold">How to read:</span> The purple line shows daily content views (left axis), while the amber line shows clicks (right axis). 
                      Rising trends indicate growing engagement. A healthy pattern shows clicks following views proportionally. 
                      Spikes may indicate successful campaigns or featured content. Use the time range dropdown to identify long-term patterns vs short-term fluctuations.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-gray-500 font-medium">No tracking data available yet</p>
                  <p className="text-gray-400 text-sm mt-1">Data will appear once users start viewing Explore content</p>
                </div>
              )}
            </div>
          )}

          {/* Performance by Type Tab */}
          {activeTab === 'performance' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Performance by Content Type</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Views & Clicks by Type</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={byTypeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" stroke="#6b7280" tick={{ fontSize: 12 }} />
                      <YAxis stroke="#6b7280" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="views" name="Views" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="clicks" name="Clicks" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Explanatory note */}
                  <div className="mt-3 p-2 bg-purple-50 rounded border border-purple-100">
                    <p className="text-xs text-purple-700">
                      <span className="font-semibold">How to read:</span> Compare bar heights to see which content types drive the most engagement. 
                      Taller purple bars = more views, taller amber bars = more clicks.
                    </p>
                  </div>
                </div>

                {/* Table */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Detailed Breakdown</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Total</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Active</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Views</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Clicks</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">CTR</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {byTypeData.map((item) => (
                          <tr key={item.type} className="hover:bg-purple-50 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: TYPE_COLORS[item.type] }}
                                />
                                <span className="text-sm font-medium text-gray-900">{item.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600">{item.total}</td>
                            <td className="px-4 py-3 text-right text-sm text-green-600 font-medium">{item.active}</td>
                            <td className="px-4 py-3 text-right text-sm text-purple-600 font-medium">{formatNumber(item.views)}</td>
                            <td className="px-4 py-3 text-right text-sm text-amber-600 font-medium">{formatNumber(item.clicks)}</td>
                            <td className="px-4 py-3 text-right text-sm text-indigo-600 font-bold">{item.ctr}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Explanatory note */}
                  <div className="mt-3 p-2 bg-green-50 rounded border border-green-100">
                    <p className="text-xs text-green-700">
                      <span className="font-semibold">How to read:</span> CTR (Click-Through Rate) = Clicks ÷ Views × 100. 
                      Higher CTR means content is more compelling. A good CTR is typically 2-5%; above 10% is excellent.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Top Performers Tab */}
          {activeTab === 'top' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Top Performing Content</h3>
              
              {getSortedPerformers().length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-gray-50 to-purple-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Rank</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Title</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                        <th 
                          className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase cursor-pointer hover:text-purple-600"
                          onClick={() => handleSort('views')}
                        >
                          Views <SortIcon field="views" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase cursor-pointer hover:text-purple-600"
                          onClick={() => handleSort('clicks')}
                        >
                          Clicks <SortIcon field="clicks" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase cursor-pointer hover:text-purple-600"
                          onClick={() => handleSort('ctr')}
                        >
                          CTR <SortIcon field="ctr" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getSortedPerformers().map((item, index) => (
                        <tr key={item.id} className="hover:bg-purple-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-sm font-bold ${
                              index === 0 ? 'text-amber-500' : 
                              index === 1 ? 'text-gray-400' : 
                              index === 2 ? 'text-orange-400' : 'text-gray-600'
                            }`}>
                              #{index + 1}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                {item.title}
                              </span>
                              {item.is_featured && (
                                <span className="text-amber-500" title="Featured">★</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span 
                              className="px-2 py-1 text-xs font-semibold rounded-full text-white"
                              style={{ backgroundColor: TYPE_COLORS[item.type] }}
                            >
                              {getTypeLabel(item.type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span 
                              className="px-2 py-1 text-xs font-semibold rounded-full"
                              style={{ 
                                backgroundColor: `${STATUS_COLORS[item.status]}20`,
                                color: STATUS_COLORS[item.status]
                              }}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-purple-600">
                            {formatNumber(item.views)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-amber-600">
                            {formatNumber(item.clicks)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-indigo-600">
                            {item.ctr}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Explanatory note */}
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs text-amber-700">
                      <span className="font-semibold">How to read:</span> Content is ranked by total views by default. Click column headers to sort by Views, Clicks, or CTR. 
                      ★ indicates featured content. High views with low CTR may need better call-to-action. High CTR with low views suggests the content resonates well but needs more visibility.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 font-medium">No content data available</p>
                </div>
              )}
            </div>
          )}

          {/* By Group Tab */}
          {activeTab === 'groups' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Performance by Group</h3>
              
              {analytics?.by_group && analytics.by_group.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {analytics.by_group.sort((a, b) => b.views - a.views).map((group) => (
                    <div 
                      key={group.group_id} 
                      className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900 truncate">{group.group_name}</h4>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Content Items</span>
                          <span className="font-medium text-gray-900">{group.content_count}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Views</span>
                          <span className="font-medium text-purple-600">{formatNumber(group.views)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Clicks</span>
                          <span className="font-medium text-amber-600">{formatNumber(group.clicks)}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                          <span className="text-gray-500">CTR</span>
                          <span className="font-bold text-indigo-600">{group.ctr}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 font-medium">No group data available</p>
                  <p className="text-gray-400 text-sm mt-1">Create groups in Explore Hub to see analytics by group</p>
                </div>
              )}
              {/* Explanatory note */}
              <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <p className="text-xs text-indigo-700">
                  <span className="font-semibold">How to read:</span> Groups are sorted by total views. Compare groups to identify which content categories perform best. 
                  &quot;Ungrouped&quot; contains content not assigned to any group. Consider grouping high-performing content together for easier management.
                </p>
              </div>
            </div>
          )}

          {/* Targeting Insights Tab */}
          {activeTab === 'targeting' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Targeting Insights</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie Chart */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Targeting Methods Distribution</h4>
                  {targetingData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={targetingData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {targetingData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No targeting data
                    </div>
                  )}
                  {/* Explanatory note */}
                  <div className="mt-3 p-2 bg-purple-50 rounded border border-purple-100">
                    <p className="text-xs text-purple-700">
                      <span className="font-semibold">How to read:</span> Shows how content is targeted. Geo-targeted = location-based, Category-targeted = by business type, Region-targeted = by UK region, Untargeted = shown to all users.
                    </p>
                  </div>
                </div>

                {/* Status Pie Chart */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Content Status Distribution</h4>
                  {byStatusData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={byStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {byStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No status data
                    </div>
                  )}
                  {/* Explanatory note */}
                  <div className="mt-3 p-2 bg-green-50 rounded border border-green-100">
                    <p className="text-xs text-green-700">
                      <span className="font-semibold">How to read:</span> Active = live content, Draft = not published, Scheduled = future start date, Expired = past end date, Archived = manually hidden. Aim for most content to be Active.
                    </p>
                  </div>
                </div>

                {/* Targeting Stats Cards */}
                <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {targetingData.map((item) => (
                    <div 
                      key={item.name}
                      className="bg-white rounded-lg p-4 border border-gray-200 text-center"
                    >
                      <div 
                        className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                        style={{ backgroundColor: `${item.color}20` }}
                      >
                        <span className="text-xl font-bold" style={{ color: item.color }}>
                          {item.value}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-700">{item.name}</p>
                    </div>
                  ))}
                </div>

                {/* Summary note */}
                <div className="lg:col-span-2 p-3 bg-gray-100 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">💡 Tip:</span> Well-targeted content typically performs better than untargeted content. 
                    Use geo-targeting for local promotions and category targeting for industry-specific offers. 
                    Monitor CTR differences between targeted and untargeted content to measure effectiveness.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExploreAnalyticsPage;
